import { Injectable } from '@angular/core';
import { HttpInterceptorService } from "../../core/services/http-interceptor.service";
import { Observable } from "rxjs/Observable";
import { ICurationUpdateRequest } from "../interfaces/curation-update-request.interface";
import { ICurationCreateRequest } from "../interfaces/curation-create-request.interface";
import { CurationSettings } from "../components/curation-dashboard/curation-setting";
import { CurationUpdateNode } from "../classes/curation-update-node";
import { assign, has, get } from 'lodash';
import { CurationUtils } from "../utils/curation-utils";
import { IspmHttpClient } from "@ispm/core/services/ispm-http-client";

@Injectable()
export class CurationService {

  public static baseUrlV0 = '/curation/v0';
  public static baseUrlV1 = '/curation/v1';
  public static urlSearchCurationInfo = '/curations/_search';
  private nodeUpdate: CurationUpdateNode;
  private readonly deploymentIdAndPathwayNameFields = "{'deploymentId':'content.bpmn.bpmnIdentifier.deploymentId','name':'content.bpmn.bpmnIdentifier.name', 'diseaseDisplay':'content.disease.display'}";

  constructor(private _http: HttpInterceptorService,
              private _ispmHttpClient: IspmHttpClient) {
  }

  public getCurationDefinition(params?: string): Observable<any[]> {
    const req: any = this._http.get('/curation' + '/curationdefinition' + (params ? '?' + params : ''), !!params)
      .map(data => data.body)
      .publish();
    req.connect();
    return req;
  }

  public getCurationWorkList(db: any, type: string, conf: any, filter: any): Observable<any[]> {
    // statuses: req.query.db,
    //   type: req.query.type,
    //   fields: req.query.fields

    const cType = (type === CurationSettings.CURATION_WORKLIST || type === CurationSettings.CURATION_MDA_WORKLIST) ? undefined : type;
    const type1 = cType != undefined ? '&type=' + cType : '';
    const baseUrl = '/curation/v1' + '/worklist/' + '?kb=' + db +
      '&fields=' + conf['ui']['worklist']['fields'] +
      '&mapping=' + JSON.stringify(conf['ui']['worklist']['worklistFieldsMapping']);

    let url = baseUrl;
    const urlWithDefaultStatusFilter = url + '&status:exact=' + conf['ui']['worklist']['statuses'];
    const urlWithDefaultFilters = urlWithDefaultStatusFilter + type1;
    if (filter) {
      const parsedFilter = JSON.parse(filter);
      const parsedFilterKeys: string[] = Object.keys(parsedFilter);
      if (parsedFilterKeys && parsedFilterKeys.length > 0) {
        if (!parsedFilterKeys.includes('status') && !parsedFilterKeys.includes('status:exact')) {
          url = urlWithDefaultStatusFilter;
        } else if (parsedFilterKeys.includes('status')) {
          url += '&status:exact=' + parsedFilter['status'];
        }

        parsedFilterKeys.forEach((key) => {
          if (key != 'status') {
            url = url + '&' + key + '=' + parsedFilter[key]
          }
        });

        if (cType) {
          url = url + type1;
        }
      } else {
        url = urlWithDefaultFilters;
      }
    } else {
      url = urlWithDefaultFilters;
    }

    const req = this._http.get(url, true)
      .map(data => {

        return data;
      })
      .map(data => data.json())
      .publish();
    req.connect();
    return req;
  }


  public createCurationItem( db: string, data: ICurationCreateRequest): Observable<any> {
    const req = this._http.post(CurationService.baseUrlV0 + '/create' + '?kb=' + db , data, true)
      .map(data => {
        const res = data.json();
        res['createCurationItemStatus'] = data.status;
        return res;
      })
      .publish();
    req.connect();
    return req;
  }

  public createCurationItemV1( db: string, data: ICurationCreateRequest, type: string): Observable<any> {
    const req = this._http.post(CurationService.baseUrlV1 + '?kb=' + db + '&type=' + type, data, true)
      .map(data => {
        const res = data.json();
        res['createCurationItemStatus'] = data.status;
        return res;
      })
      .publish();
    req.connect();
    return req;
  }

  public getWorkflowRequest( id: string): Observable<any> {
    const req = this._http.get('/workflow/find?id=' + id, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .publish();
    req.connect();
    return req;
  }

  public getCurationItemByKeys(db: string, data: ICurationCreateRequest): Observable<any> {
    const req = this._http.post(CurationService.baseUrlV0 + '/get' + '?kb=' + db , data, true)
        .map(data => data.json())
        .map(item => {
            return item;
        })
        .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public selectCurationItemByKeys(db: string, data: ICurationCreateRequest): Observable<any> {
    const req = this._http.post(CurationService.baseUrlV1 + '/get' + '?kb=' + db , data, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public getCurationItemByKeysV1(db: string, data: any, type: string): Observable<any> {
    const req = this._http.post(CurationService.baseUrlV1 + '/_search' + '?kb=' + db + '&type=' + type, data, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public getCurationItem(curationId: string, revisionId: string, db: string): Observable<any> {
    let url = CurationService.baseUrlV0;
    url = url + '/' + curationId;
    if (revisionId != null) {
      url = url + '/' + revisionId;
    }
    url += '?kb=' + db;

    const req = this._http.get(url , true, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .publish();
    req.connect();
    return req;
  }

  public searchCurationInfo(context: string, type: string, query: any): Observable<any> {
    const params = Object.assign({ }, query, { 'context': context, 'type': type });
    const req = this._http.get(CurationService.urlSearchCurationInfo , params, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .publish();
    req.connect();
    return req;
  }

  public getCurationItemV1(curationId: string, revisionId: string, db: string, type: string, toggleLoader: boolean = true): Observable<any> {
    let url = CurationService.baseUrlV1;
    url = url + '/' + curationId;
    if (revisionId != null) {
      url = url + '/' + revisionId;
    }
    url += '?kb=' + db + '&type=' + type;

    const req = this._http.get(url , true, toggleLoader)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public updateCurationItem(curationId: string, data: ICurationUpdateRequest, db: string): Observable<any> {
    const req = this._http.put(CurationService.baseUrlV0 + '/' + curationId + '?kb=' + db, data, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public updateCurationItemV1(curationId: string, data: ICurationUpdateRequest, db: string, type: string, status: string): Observable<any> {
    const req = this._http.put(CurationService.baseUrlV1 + '/' + curationId + '?kb=' + db + '&type=' + type + '&status=' + status , data, false) // set toggleLoader to false because curation editors handle the loader by theirselves
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public discardCurrentRevision(curationId: string, data: ICurationUpdateRequest, db: string, type: string, status: string): Observable<any> {
    const req = this._http.put(CurationService.baseUrlV1 + '/discardCurrentRevision/' + curationId + '?kb=' + db + '&type=' + type + '&status=' + status , data, false) // set toggleLoader to false because curation editors handle the loader by theirselves
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public updateCurationWhiteListItemV1(curationId: string, data: ICurationUpdateRequest, db: string, type: string): Observable<any> {
    const req = this._http.put(CurationService.baseUrlV1 + '/' + curationId + '?kb=' + db + '&type=' + type + '&updateWhitelistFields=true', data, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }


  public updateHasNotes(curationInfoId: string, db: string, type: string): Observable<any> {
    const req = this._http.put(CurationService.baseUrlV1 + '/hasNotes/' + curationInfoId + '?kb=' + db + '&type=' + type, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .publish();
    req.connect();
    return req;
  }

  public cloneCurationItem(db: string, curationId: string, revisionId: string, disease: string, opinion: any): Observable<any> {
    let url = CurationService.baseUrlV0;
    url = url + '/clone';
    url = url + '/' + curationId;
    if (revisionId != null) {
      url = url + '/' + revisionId;
    }
    url = url + '?kb=' + db;
    const data = {
      disease: disease['code'],
      diseaseDisplay: disease['display'],
      opinion: opinion
    };
    const req = this._http.post(url, data, true)
      .map(data => {
        const res = data.json();
        res['cloneCurationItemStatus'] = data.status;
        return res;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public getCurationWorklistValuesets(valuesetIds: string[]): Observable<any[]> {
    let req: any;
    req = this._http.get('/curation/v1/getCurationWorklistValuesets', { "_id": valuesetIds.join(',') })
      .map(data => data.body)
      .publish();
    req.connect();
    return req;
  }

  // The method is in called by the client before archving a curation item that can have related entities
  public getRelatedEntities(curationItemId: string, db: string, available?: boolean): Observable<any> {
    const req = this._http.get('curation/v1/getRelatedEntities',
      {
        'curationItemId': curationItemId,
        'kb': db,
        _fieldslist: "content.type,status,context,revisionName,latest",
        'status': '!=archived',
        'available': available ? available : false
      })
      .map(jsonResponse => {
        const res = jsonResponse.json();
        return res;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public downloadFile(fileId: string): Observable<any> {
    const params: any = {
      reportId: fileId,
      isJson: true
    };
    const req = this._http.get('/workflow/report', params)
      .map(jsonResponse => {
        const res = jsonResponse.json();
        return res;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public setCurationItemOwner(db: string, curationItemId: string, revisionId: string, workflow: string,
                              ownerId: string, type: string, status: string): Observable<any> {
    const url = `${CurationService.baseUrlV1}/setCurationItemOwner?kb=${db}`;
    const data = {
      revisionId,
      workflow,
      curationItemId,
      ownerId,
      type,
      status
    };

    const req = this._http.post(url, data, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  public submitCurationItemForReview(db: string, curationItemId: string, revisionId: string, workflow: string,
                                     activePractitionerId: string, reviewerId: string, type: string,
                                     status: string): Observable<any> {
    let url = CurationService.baseUrlV1;
    url = url + '/submitCurationItemForReview';
    url = url + '?kb=' + db;
    const data = {
      revision: revisionId,
      workflow: workflow,
      curationItemId: curationItemId,
      activePractitioner: activePractitionerId,
      reviewerId,
      type,
      status
    };

    const req = this._http.post(url, data, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .catch(err => {
        return Observable.of(err);
      })
      .publish();
    req.connect();
    return req;
  }

  listCurationNamesByName() {
    let url = CurationService.baseUrlV1;
    url = url + '/listCurationNamesByName';
    const req = this._http.get(url , true, true)
      .map(data => {
        return data.json();
      })
      .map(item => {
        return item;
      })
      .publish();
    req.connect();
    return req;
  }

  listCurationNamesByDisciplineAndDisease(discipline: number, diseaseCodes: number[]) {
    let url = CurationService.baseUrlV1;
    url = url + '/listCurationNames';

    const params = {
      discipline: discipline,
      type: "pathwaybpmn",
      disease: diseaseCodes.join(","),
      calcstatus: true,
      fieldsMapping: this.deploymentIdAndPathwayNameFields,
      _count: 101
    };

    return this._http.get(url , params, false);
  }

  listCurationNames(db: string) {
    let url = CurationService.baseUrlV1;
    url = url + '/listCurationNames';
    url += '?kb=' + db;

    const req = this._http.get(url , true, true)
      .map(data => data.json())
      .map(item => {
        return item;
      })
      .publish();
    req.connect();
    return req;
  }

  getCurationDataList( queryParams: { [key: string]: string}): Observable<any> {
    let url = CurationService.baseUrlV1;
    url = url + '/listCurationData';
    return this._http.get(url, queryParams);
  }

  setNodeUpdate(nodeUpdate: CurationUpdateNode) {
    this.nodeUpdate = nodeUpdate;
  }

  getNodeUpdate(): CurationUpdateNode {
    return this.nodeUpdate;
  }

  getVsMap(vs: Array<any>): Map<string, any[]> {
    const vsMap = new Map<string, any[]>();
    vs.forEach(v => {
      vsMap.set(v.id, v.concept);
    });
    return vsMap;
  }

  manipulateCurationData(result: any): void {
    if (result['privateData.tier']) {
      result['privateData.tier'] = 'Tier' + result['privateData.tier'];
    }

    if (result['privateData.type']) {
      switch (result['privateData.type']) {
        case 'regimen':
        case 'treatmentprotocol':
          assign(result, {'name': result['content.regimenName']});
          break;
        case 'treatmentplan':
          assign(result, {'name': result['content.treatmentPlanName']});
          break;
        case 'placement':
          assign(result, {'name': result['content.placementName']});
          break;
        case 'aberration':
          assign(result, {'name': result['content.metadata.aberrationKey']});
          break;
        case 'pathway':
        case 'pathwaybpmn':
          assign(result, {'name': result['content.title']});
          break;
        case 'clinicaltrial':
          assign(result, {'name': result['content.protocolNumber']});
          break;
        case 'summary':
          if (result['content.title']) {
            assign(result, {'name': result['content.title']});
          } else if (result['content.metadata.aberrationKey']) {
            assign(result, {'name': result['content.metadata.aberrationKey']});
          } else if (result['content.regimenName']) {
            assign(result, {'name': result['content.regimenName']});
          } else if (result['content.treatmentPlanName']) {
            assign(result, {'name': result['content.treatmentPlanName']});
          } else if (result['content.protocolNumber']) {
            assign(result, {'name': result['content.protocolNumber']});
          } else {
            assign(result, {'name': result['content.name']});
          }
          break;
      }
    }
  }


}

